import { useQuery } from "@tanstack/react-query";
import { useState, useCallback, useMemo, useRef } from "react";
import { searchService } from "../../api/search";
import { SearchFilters, Property } from "../../types/search";
import { analyticsService } from "../../services/analyticsService";
import { useTenant } from "../../contexts/TenantContext";

const PAGE_SIZE = 20;

/**
 * useDirectSearchQuery - A hook for direct search with server-side pagination
 *
 * This hook is used when no query is provided, and pagination is handled on the server side
 * by passing an offset parameter to the API.
 */
export const useDirectSearchQuery = (pageSize = 20) => {
  const { config } = useTenant();
  const [filters, setFilters] = useState<
    SearchFilters & {
      offset?: number;
      initialServerProperties?: Property[];
    }
  >(() => ({
    query: "",
    location: { city: "", state: "" },
    tenant_id: "",
  }));
  const [currentPage, setCurrentPage] = useState(1);
  const [allResults, setAllResults] = useState<Property[]>([]);

  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["properties", filters, currentPage],
    queryFn: async ({ signal }) => {
      // Use explicit offset from filters if provided, otherwise calculate based on currentPage
      const offset =
        filters.offset !== undefined
          ? filters.offset
          : (currentPage - 1) * pageSize;

      const response = await searchService.searchProperties(
        {
          ...filters,
          number: pageSize, // Only fetch the current page size
          offset: offset, // Pass offset for server-side pagination
        },
        0,
        pageSize,
        signal
      );

      // Track search event
      if (filters.query || filters.location?.city) {
        analyticsService.trackEvent({
          tenantId: config?.id || filters.tenant_id,
          orgId: config?.orgData?.id,
          eventType: "search",
          category: "Search",
          action: "Search Query",
          label: filters.query || filters.location?.city || "browse",
          value: response.totalMatches,
          data: {
            searchQuery: filters.query,
            searchFilters: filters,
            resultCount: response.totalMatches,
            city: filters.location?.city,
            state: filters.location?.state,
            minPrice: filters.filters?.min_price,
            maxPrice: filters.filters?.max_price,
            propertySubType: filters.filters?.property_sub_type,
            page: currentPage,
            offset: offset,
          },
        });
      }

      return {
        properties: response.properties,
        totalAvailable: response.totalMatches,
        timestamp: response.timestamp,
      };
    },
    enabled: !!filters.tenant_id, // Only run query if tenant_id is set
  });

  // Update all results when new data is fetched
  useMemo(() => {
    if (data?.properties) {
      if (currentPage === 1) {
        // If we have initialServerProperties, use them as the base and append the new results
        if (
          filters.initialServerProperties &&
          filters.initialServerProperties.length > 0
        ) {
          console.log("Using initialServerProperties as base for results");
          setAllResults([
            ...filters.initialServerProperties,
            ...data.properties,
          ]);
        } else {
          // Reset results if it's the first page and no initialServerProperties
          setAllResults(data.properties);
        }
      } else {
        // Append new results to existing ones
        setAllResults((prev) => [...prev, ...data.properties]);
      }
    }
  }, [data?.properties, currentPage, filters.initialServerProperties]);

  const totalAvailable = data?.totalAvailable || 0;
  const hasMore = allResults.length < totalAvailable;

  const search = useCallback(
    (
      newFilters: SearchFilters & {
        offset?: number;
        number?: number;
        initialServerProperties?: Property[];
      }
    ) => {
      // If offset is provided, we're doing a special search (e.g., loading page 2 after server properties)
      // In this case, we want to keep the offset as is and not calculate it based on currentPage
      const hasExplicitOffset = newFilters.offset !== undefined;

      if (!hasExplicitOffset) {
        setCurrentPage(1); // Reset to first page on normal search
      }

      setFilters({
        ...newFilters,
      });
    },
    []
  );

  const loadMore = useCallback(() => {
    if (hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore]);

  const retryFailedRequest = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    results: allResults,
    loading,
    error: error ? (error as Error).message : null,
    hasMore,
    filters,
    search,
    loadMore,
    totalAvailable,
    retryFailedRequest,
    timestamp: data?.timestamp,
    currentPage,
  };
};
